import { logger } from './logger';

// In-memory caches to avoid redundant geocoding calls
const forwardCache = new Map(); // key: normalized address -> { lat, lng, formatted_address }
const reverseCache = new Map(); // key: "lat,lng" -> { lat, lng, formatted_address }

const normalizeAddressKey = (address) =>
  (address || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');

const normalizeCoordinateKey = (lat, lng) => {
  const parse = (value) => {
    const num = Number(value);
    return Number.isFinite(num) ? num.toFixed(6) : 'NaN';
  };

  return `${parse(lat)},${parse(lng)}`;
};

export const initializeGeocoder = () => {
  return new Promise((resolve, reject) => {
    if (window.google && window.google.maps && window.google.maps.Geocoder) {
      resolve(new window.google.maps.Geocoder());
    } else {
      reject(new Error('Google Maps API not loaded'));
    }
  });
};

export const geocodeAddress = async (geocoder, address, country = 'us') => {
  if (!geocoder || !address || !address.trim()) {
    return null;
  }

  const cacheKey = normalizeAddressKey(address);
  if (forwardCache.has(cacheKey)) {
    return forwardCache.get(cacheKey);
  }

  try {
    const result = await new Promise((resolve) => {
      geocoder.geocode(
        {
          address: address.trim(),
          ...(country ? { componentRestrictions: { country } } : {}),
        },
        (results, status) => {
          if (status === window.google.maps.GeocoderStatus.OK && results && results.length) {
            const location = results[0].geometry.location;
            const resolved = {
              lat: location.lat(),
              lng: location.lng(),
              formatted_address: results[0].formatted_address || address.trim(),
            };
            forwardCache.set(cacheKey, resolved);
            resolve(resolved);
          } else {
            logger.error(`Geocoding failed for address "${address}": ${status}`);
            resolve(null);
          }
        }
      );
    });

    return result;
  } catch (error) {
    logger.error('Error in geocodeAddress:', error);
    return null;
  }
};

export const reverseGeocode = async (geocoder, lat, lng) => {
  if (!geocoder || lat === null || lat === undefined || lng === null || lng === undefined) {
    return null;
  }

  const cacheKey = normalizeCoordinateKey(lat, lng);
  if (reverseCache.has(cacheKey)) {
    return reverseCache.get(cacheKey);
  }

  const latNum = Number(lat);
  const lngNum = Number(lng);
  if (!Number.isFinite(latNum) || !Number.isFinite(lngNum)) {
    logger.error(`Invalid coordinates for reverse geocoding: (${lat}, ${lng})`);
    return null;
  }

  try {
    const result = await new Promise((resolve) => {
      geocoder.geocode(
        {
          location: new window.google.maps.LatLng(latNum, lngNum),
        },
        (results, status) => {
          if (status === window.google.maps.GeocoderStatus.OK && results && results.length) {
            const preferred = results.find((res) =>
              res.types?.some((type) =>
                ['street_address', 'premise', 'establishment'].includes(type)
              )
            );
            const formattedAddress = (preferred || results[0]).formatted_address;
            const resolved = {
              lat: latNum,
              lng: lngNum,
              formatted_address: formattedAddress,
            };
            reverseCache.set(cacheKey, resolved);
            resolve(resolved);
          } else {
            logger.error(`Reverse geocoding failed for coordinates (${latNum}, ${lngNum}): ${status}`);
            resolve(null);
          }
        }
      );
    });

    return result;
  } catch (error) {
    logger.error('Error in reverseGeocode:', error);
    return null;
  }
};

export const batchGeocodeAddresses = async (geocoder, addresses, country = 'us', poolLimit = 3) => {
  if (!Array.isArray(addresses) || !addresses.length) {
    return [];
  }

  const tasks = addresses.map((address) => () => geocodeAddress(geocoder, address, country));
  return promisePool(tasks, poolLimit);
};

export const batchReverseGeocode = async (geocoder, coordinates, poolLimit = 3) => {
  if (!Array.isArray(coordinates) || !coordinates.length) {
    return [];
  }

  const tasks = coordinates.map((coord) => () => reverseGeocode(geocoder, coord.lat, coord.lng));
  return promisePool(tasks, poolLimit);
};

const promisePool = async (tasks, poolLimit) => {
  const results = [];
  const executing = new Set();

  for (const task of tasks) {
    if (typeof task !== 'function') {
      logger.error('Geocoding promise pool received non-function task:', task);
      continue;
    }

    const promise = Promise.resolve()
      .then(() => task())
      .finally(() => executing.delete(promise));

    executing.add(promise);
    results.push(promise);

    if (executing.size >= poolLimit) {
      await Promise.race(executing);
    }
  }

  return Promise.all(results);
};

export const clearGeocodeCache = () => {
  forwardCache.clear();
  reverseCache.clear();
};
